// @ts-nocheck
/**
 * Period picker on the FinalMappingCard.
 *
 * One control, two states:
 *
 *   1. A centered iOS-style segmented control with a coral pill that
 *      *slides* between presets. Exactly one preset is active at any
 *      time. Mirrors the visual treatment + spring motion of
 *      ``.ios-theme-switch`` in AccountOverview so the picker reads as
 *      part of the same family of controls.
 *
 *   2. A natural-language summary below the control that describes what
 *      the export will contain. No clickable chips — the segmented
 *      control is the only way to change the selection.
 *
 * Presets are filtered so we never show two that would produce the
 * same selection. On a sheet with only primary + aggregate columns the
 * user sees just "Primary only" and "Primary + Aggregates" (no "All
 * columns" — it'd duplicate the second option).
 */

import React, { useMemo } from 'react';

import type { FinalMappingPeriod, PeriodClassification } from './types';

interface PeriodPickerProps {
  periods: FinalMappingPeriod[];
  selectedIndices: number[];
  onChange: (next: number[]) => void;
}

type PresetKey = 'primary' | 'primary_and_aggregates' | 'all';

function classificationOf(p: FinalMappingPeriod): PeriodClassification {
  return (p.classification ?? 'unknown') as PeriodClassification;
}

function indicesOfClass(
  periods: FinalMappingPeriod[],
  cls: PeriodClassification,
): number[] {
  return periods
    .filter((p) => classificationOf(p) === cls)
    .map((p) => p.period_index);
}

function sortByOrder(
  displayable: FinalMappingPeriod[],
  set: Set<number>,
): number[] {
  return displayable
    .filter((p) => set.has(p.period_index))
    .map((p) => p.period_index);
}

/** Join a short list of names for a NL sentence: "A", "A and B",
 *  "A, B and C". Empty list returns the empty string. */
function joinForSentence(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : plural || `${singular}s`;
}

function buildSummary(
  active: PresetKey,
  primary: FinalMappingPeriod[],
  aggregate: FinalMappingPeriod[],
  comparative: FinalMappingPeriod[],
  unknown: FinalMappingPeriod[],
): string {
  const aggNames = aggregate.map((p) => p.display_name).filter(Boolean);
  const compNames = comparative.map((p) => p.display_name).filter(Boolean);
  const aggPretty = aggNames.length > 0 && aggNames.length <= 3
    ? ` (${joinForSentence(aggNames)})`
    : '';
  const compPretty = compNames.length > 0 && compNames.length <= 3
    ? ` (${joinForSentence(compNames)})`
    : '';

  if (active === 'primary') {
    if (primary.length === 0) {
      return 'No primary columns were detected on this sheet — the export will be empty until you switch presets.';
    }
    const skipped: string[] = [];
    if (aggregate.length > 0) {
      skipped.push(
        `${aggregate.length} aggregate ${pluralize(aggregate.length, 'column')}${aggPretty}`,
      );
    }
    if (comparative.length > 0) {
      skipped.push(
        `${comparative.length} comparative ${pluralize(comparative.length, 'column')}${compPretty}`,
      );
    }
    if (unknown.length > 0) {
      skipped.push(
        `${unknown.length} unclassified ${pluralize(unknown.length, 'column')}`,
      );
    }
    const head = `Your export will include all ${primary.length} primary ${pluralize(primary.length, 'period')} detected on this sheet.`;
    if (skipped.length === 0) return head;
    return `${head} The ${joinForSentence(skipped)} will be excluded.`;
  }

  if (active === 'primary_and_aggregates') {
    const total = primary.length + aggregate.length;
    const head = `Your export will include all ${primary.length} primary ${pluralize(primary.length, 'period')} plus ${aggregate.length} aggregate ${pluralize(aggregate.length, 'column')}${aggPretty} — ${total} columns in total.`;
    const skipped: string[] = [];
    if (comparative.length > 0) {
      skipped.push(
        `${comparative.length} comparative ${pluralize(comparative.length, 'column')}${compPretty}`,
      );
    }
    if (unknown.length > 0) {
      skipped.push(
        `${unknown.length} unclassified ${pluralize(unknown.length, 'column')}`,
      );
    }
    if (skipped.length === 0) return head;
    return `${head} The ${joinForSentence(skipped)} will be excluded.`;
  }

  // 'all'
  const total = primary.length + aggregate.length + comparative.length + unknown.length;
  const parts: string[] = [];
  if (primary.length > 0)
    parts.push(`${primary.length} primary ${pluralize(primary.length, 'period')}`);
  if (aggregate.length > 0)
    parts.push(`${aggregate.length} aggregate ${pluralize(aggregate.length, 'column')}${aggPretty}`);
  if (comparative.length > 0)
    parts.push(`${comparative.length} comparative ${pluralize(comparative.length, 'column')}${compPretty}`);
  if (unknown.length > 0)
    parts.push(`${unknown.length} unclassified ${pluralize(unknown.length, 'column')}`);
  return `Your export will include every column on this sheet — ${total} in total: ${joinForSentence(parts)}.`;
}

const PeriodPicker: React.FC<PeriodPickerProps> = ({
  periods,
  selectedIndices,
  onChange,
}) => {
  // 'empty' columns are dropped before anything else — they carry no
  // values and shouldn't be counted anywhere on the picker.
  const displayable = useMemo(
    () => (periods || []).filter((p) => classificationOf(p) !== 'empty'),
    [periods],
  );

  const primary = useMemo(
    () => displayable.filter((p) => classificationOf(p) === 'primary'),
    [displayable],
  );
  const aggregate = useMemo(
    () => displayable.filter((p) => classificationOf(p) === 'aggregate'),
    [displayable],
  );
  const comparative = useMemo(
    () => displayable.filter((p) => classificationOf(p) === 'comparative'),
    [displayable],
  );
  const unknown = useMemo(
    () => displayable.filter((p) => classificationOf(p) === 'unknown'),
    [displayable],
  );

  const primaryIndices = useMemo(() => primary.map((p) => p.period_index), [primary]);
  const aggregateIndices = useMemo(
    () => aggregate.map((p) => p.period_index),
    [aggregate],
  );
  const allIndices = useMemo(
    () => displayable.map((p) => p.period_index),
    [displayable],
  );

  const selectedSet = useMemo(() => new Set(selectedIndices), [selectedIndices]);

  // Pick the preset that matches the current selection. Falls back to
  // 'primary' so the sliding pill always has a defined position to
  // animate from on first render.
  const activePreset: PresetKey = useMemo(() => {
    const setEquals = (candidate: number[]) =>
      candidate.length === selectedSet.size &&
      candidate.every((i) => selectedSet.has(i));
    if (setEquals(allIndices) && (comparative.length > 0 || unknown.length > 0))
      return 'all';
    if (
      setEquals([...primaryIndices, ...aggregateIndices]) &&
      aggregate.length > 0
    )
      return 'primary_and_aggregates';
    if (setEquals(primaryIndices)) return 'primary';
    // Selection doesn't match any preset (e.g. the user came back to a
    // legacy stored selection). Fall back to the closest preset that
    // would render the same selection, defaulting to 'primary'.
    return 'primary';
  }, [
    selectedSet,
    primaryIndices,
    aggregateIndices,
    allIndices,
    aggregate.length,
    comparative.length,
    unknown.length,
  ]);

  // Filter presets so we never offer two that would produce the same
  // selection. On a primary-only sheet "Primary + Aggregates" and "All
  // columns" both collapse into "Primary only" — the picker won't render
  // at all in that case (handled below).
  const presets: Array<{ key: PresetKey; label: string }> = useMemo(() => {
    const out: Array<{ key: PresetKey; label: string }> = [
      { key: 'primary', label: 'Primary only' },
    ];
    if (aggregate.length > 0) {
      out.push({
        key: 'primary_and_aggregates',
        label: 'Primary + Aggregates',
      });
    }
    if (comparative.length > 0 || unknown.length > 0) {
      out.push({ key: 'all', label: 'All columns' });
    }
    return out;
  }, [aggregate.length, comparative.length, unknown.length]);

  const applyPreset = (key: PresetKey) => {
    if (key === 'primary') {
      onChange(primaryIndices);
    } else if (key === 'primary_and_aggregates') {
      onChange(
        sortByOrder(
          displayable,
          new Set([...primaryIndices, ...aggregateIndices]),
        ),
      );
    } else {
      onChange(allIndices);
    }
  };

  if (displayable.length === 0) return null;
  // Only one viable preset → nothing to choose, hide the picker.
  if (presets.length <= 1) return null;

  const activeIndex = Math.max(
    0,
    presets.findIndex((p) => p.key === activePreset),
  );
  const summary = buildSummary(activePreset, primary, aggregate, comparative, unknown);

  return (
    <div className="fmc-picker">
      <div className="fmc-picker-header">
        <span className="fmc-picker-title">Columns to include in export</span>
      </div>

      <div className="fmc-preset-wrap">
        <div
          className="fmc-preset-segmented"
          role="radiogroup"
          aria-label="Quick selection presets"
          style={{
            // Read by the CSS to size and position the sliding pill.
            // Equal-width grid columns give the pill a stable target
            // width so a single transform interpolates cleanly between
            // segments.
            ['--preset-count' as any]: presets.length,
            ['--preset-active' as any]: activeIndex,
          }}
        >
          <span className="fmc-preset-pill" aria-hidden />
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              role="radio"
              aria-checked={activePreset === p.key}
              className={`fmc-preset-option ${
                activePreset === p.key ? 'is-active' : ''
              }`}
              onClick={() => applyPreset(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <p className="fmc-picker-summary">{summary}</p>
    </div>
  );
};

export default PeriodPicker;
